import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FileVideo,
  FileAudio,
  AlertCircle,
  CheckCircle2,
  Link2,
  Loader2, UploadCloud } from 'lucide-react';
import { Button, Modal, Card } from './UIComponents';
import { SearchableSelect } from './SearchableSelect';
import { LinkImportPlatformChips } from './LinkImportPlatformChips';
import { UploadStep } from '../types';
import { useNavigate } from 'react-router-dom';
import { useFiles } from '../hooks/useFiles';
import * as transcriptionsApi from '../api/transcriptions';
import * as billingApi from '../api/billing';
import { handleApiError } from '../api/client';
import MediaDropzone from './MediaDropzone';
import { estimateMediaDurationSeconds, prepareAudioUpload } from '../utils/audioExtraction';
import { calculateFileHash } from '../utils/file-hash';
import { assertUploadAllowedByQuota } from '../utils/uploadQuotaPrecheck';
import {
  createLinkImport,
  getLinkImport,
  previewLinkImport,
  resolveLinkImportThumbnailUrl,
  type LinkImport,
  type LinkImportPreview,
} from '../api/linkImports';
import {
  getLinkImportProviderLabel,
  isSupportedLinkImportUrl,
} from '../utils/linkImportPlatforms';

interface UploadFlowProps {
  isOpen: boolean;
  onClose: () => void;
  embedded?: boolean;
}

function formatLinkDuration(seconds: number) {
  const totalSeconds = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}

const UploadFlow: React.FC<UploadFlowProps> = ({ isOpen, onClose, embedded = false }) => {
  const [step, setStep] = useState<UploadStep>(UploadStep.SELECT_FILE);
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState(0);
  const [activeStageIndex, setActiveStageIndex] = useState(0);
  const [instantUpload, setInstantUpload] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [entryMode, setEntryMode] = useState<'file' | 'link'>('file');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkPreview, setLinkPreview] = useState<LinkImportPreview | null>(null);
  const [linkImport, setLinkImport] = useState<LinkImport | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [isPreviewingLink, setIsPreviewingLink] = useState(false);
  const [isStartingLinkImport, setIsStartingLinkImport] = useState(false);
  const [options, setOptions] = useState({
    language: 'auto',
    speakerDetection: false
  });

  const navigate = useNavigate();
  const { uploadFile } = useFiles();
  const canPreviewLink = useMemo(() => isSupportedLinkImportUrl(linkUrl), [linkUrl]);

  const uploadStages = [
    { label: 'Converting to audio', duration: 0 },
    { label: 'Calculating file hash', duration: 0 },
    { label: instantUpload ? 'Instant upload' : 'Uploading to cloud storage', duration: 0 }, // Real upload - progress tracked
    { label: 'Confirming upload', duration: 500 },
    { label: 'Creating transcription job', duration: 500 },
    { label: 'Queuing for transcription', duration: 300 },
  ];

  useEffect(() => {
    if (!linkImport || linkImport.status === 'completed' || linkImport.status === 'failed' || linkImport.status === 'cancelled') {
      return;
    }

    const timer = window.setInterval(async () => {
      try {
        const latest = await getLinkImport(linkImport.id);
        setLinkImport(latest);
        if (latest.transcription_id) {
          window.clearInterval(timer);
          onClose();
          navigate(`/transcription/${latest.transcription_id}`);
        }
      } catch {
        setLinkError('Unable to refresh media import status.');
      }
    }, 2000);

    return () => window.clearInterval(timer);
  }, [linkImport, navigate, onClose]);

  useEffect(() => {
    if (!linkPreview || linkPreview.metadata_status !== 'pending') {
      return;
    }
    const previewUrl = linkPreview.normalized_url || linkUrl;
    let attempts = 0;
    const timer = window.setInterval(async () => {
      attempts += 1;
      try {
        const preview = await previewLinkImport(previewUrl);
        setLinkPreview(preview);
        if (preview.metadata_status !== 'pending' || attempts >= 15) {
          window.clearInterval(timer);
        }
      } catch {
        if (attempts >= 3) {
          window.clearInterval(timer);
        }
      }
    }, 2000);
    return () => window.clearInterval(timer);
  }, [linkPreview, linkUrl]);

  const previewLink = useCallback(async () => {
    setLinkError(null);
    setLinkPreview(null);
    setLinkImport(null);
    if (!isSupportedLinkImportUrl(linkUrl)) {
      setLinkError('Paste a valid supported media link.');
      return;
    }

    setIsPreviewingLink(true);
    try {
      const preview = await previewLinkImport(linkUrl);
      setLinkPreview(preview);
    } catch {
      setLinkError('This media link cannot be previewed right now.');
    } finally {
      setIsPreviewingLink(false);
    }
  }, [linkUrl]);

  const startLinkImport = useCallback(async () => {
    if (!linkPreview) return;
    setLinkError(null);
    setIsStartingLinkImport(true);
    try {
      const created = await createLinkImport({
        url: linkPreview.normalized_url || linkUrl,
        language: options.language === 'auto' ? undefined : options.language,
        enable_diarization: options.speakerDetection,
      });
      setLinkImport(created);
    } catch {
      setLinkError('Unable to start media import. Check your quota or try another link.');
    } finally {
      setIsStartingLinkImport(false);
    }
  }, [linkPreview, linkUrl, options.language, options.speakerDetection]);

  const startUpload = async () => {
    if (!file) return;

    setStep(UploadStep.UPLOADING);
    setActiveStageIndex(0);
    setProgress(0);
    setInstantUpload(false);
    setUploadError(null);

    try {
      // Best-effort quota precheck to avoid expensive work + uploading when transcription will be rejected.
      let estimatedDurationSeconds: number | undefined;
      try {
        estimatedDurationSeconds = await estimateMediaDurationSeconds(file);
      } catch {
        estimatedDurationSeconds = undefined;
      }

      let quota: Awaited<ReturnType<typeof billingApi.getQuota>> = null;
      try {
        quota = await billingApi.getQuota();
      } catch {
        quota = null;
      }
      if (quota) {
        assertUploadAllowedByQuota(quota, estimatedDurationSeconds);
      }

      // Stage 0: Prepare audio upload
      setActiveStageIndex(0);
      const prepared = await prepareAudioUpload(file, (progress) => {
        const stageWeight = 10;
        const normalized = Math.max(0, Math.min(1, progress.progress));
        if (progress.stage === 'loading') {
          setProgress(Math.min(stageWeight * 0.5, normalized * stageWeight * 0.5));
          return;
        }
        setProgress(stageWeight * 0.5 + (normalized * stageWeight * 0.5));
      });
      setProgress(10);

      // Stage 1: Calculate hash (for dedup + instant upload)
      setActiveStageIndex(1);
      const { hashHex } = await calculateFileHash(prepared.file, (hashProgress) => {
        // Map hash progress to 10-20% range
        setProgress(10 + (hashProgress * 0.1));
      });
      setProgress(20);

      // Stage 2: Upload file (real upload with progress tracking)
      setActiveStageIndex(2);
      const uploadedFile = await uploadFile(prepared.file, (uploadProgress) => {
        // Map upload progress to 20-60% range
        setProgress(20 + (uploadProgress * 0.4));
      }, prepared.estimatedDurationSeconds, file.name, hashHex ?? undefined, () => {
        setInstantUpload(true);
        setActiveStageIndex(3);
        setProgress(60);
      }, undefined, true);
      setProgress(60);

      // Stage 3: Confirming upload
      setActiveStageIndex(3);
      await new Promise(r => setTimeout(r, 500));
      setProgress(70);

      // Stage 4: Create transcription job
      setActiveStageIndex(4);
      const transcription = await transcriptionsApi.createTranscription({
        file_id: uploadedFile.id,
        language: options.language === 'auto' ? undefined : options.language,
        options: {
          speaker_diarization: options.speakerDetection,
        },
      });
      setProgress(90);

      // Stage 5: Queuing
      setActiveStageIndex(5);
      await new Promise(r => setTimeout(r, 300));
      setProgress(100);

      setStep(UploadStep.COMPLETE);

      // Navigate to transcription page
      setTimeout(() => {
        onClose();
        navigate(`/transcription/${transcription.id}`);
      }, 2000);
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadError(handleApiError(error));
      setStep(UploadStep.CONFIRM_INFO); // Go back to confirm step to allow retry
    }
  };

  const renderContent = () => {
    switch (step) {
      case UploadStep.SELECT_FILE:
        return (
          <div className="space-y-6">
            <div className="flex justify-center">
              <div role="tablist" className="seg-group">
                <button type="button" role="tab" aria-selected={entryMode === 'file'} onClick={() => setEntryMode('file')} className="seg inline-flex items-center gap-1.5">
                  <UploadCloud className="h-3.5 w-3.5" />
                  Upload File
                </button>
                <button type="button" role="tab" aria-selected={entryMode === 'link'} onClick={() => setEntryMode('link')} className="seg inline-flex items-center gap-1.5">
                  <Link2 className="h-3.5 w-3.5" />
                  Paste Link
                </button>
              </div>
            </div>

            {entryMode === 'file' ? (
              <MediaDropzone
                embedded={embedded}
                onFileSelected={(selectedFile) => {
                  setFile(selectedFile);
                  setStep(UploadStep.CONFIRM_INFO);
                }}
              />
            ) : (
              <div className="text-center space-y-5">
                <div>
                  <p className="text-sm sm:text-base leading-relaxed text-slate-600 dark:text-zinc-400">
                    Paste a media link to preview content before transcription.
                  </p>
                  <div className="mt-4 flex items-center justify-center gap-2 text-sm font-medium text-slate-500 dark:text-zinc-400">
                    <Link2 className="h-3.5 w-3.5" />
                    Supported platforms
                  </div>
                  <LinkImportPlatformChips />
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <Link2 className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      value={linkUrl}
                      onChange={(event) => {
                        setLinkUrl(event.target.value);
                        setLinkPreview(null);
                        setLinkImport(null);
                        setLinkError(null);
                      }}
                      placeholder="Paste a YouTube, TikTok, Instagram, Facebook, or X link"
                      className="w-full h-12 rounded-full border border-transparent bg-card pl-11 pr-4 text-sm text-slate-900 dark:text-zinc-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <Button
                    className="h-12 px-6 text-sm"
                    onClick={previewLink}
                    disabled={!canPreviewLink || isPreviewingLink || isStartingLinkImport}
                  >
                    {isPreviewingLink && <Loader2 className="h-4 w-4 animate-spin" />}
                    Search
                  </Button>
                </div>

              {linkError && (
                <div className="rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 px-3 py-2 text-sm text-red-700 dark:text-red-300 text-left">
                  {linkError}
                </div>
              )}

              {linkPreview && (
                <div className="space-y-4 pt-1">
                  <div className="mx-auto max-w-2xl text-center">
                    <p className="text-base sm:text-lg font-semibold leading-snug text-slate-950 dark:text-white line-clamp-3">
                      {linkPreview.title || `${getLinkImportProviderLabel(linkPreview.provider)} media`}
                    </p>
                    <div className="mt-2 flex items-center justify-center gap-2 text-sm font-medium text-slate-500 dark:text-zinc-400">
                      {linkPreview.metadata_status === 'pending' && <Loader2 className="h-4 w-4 animate-spin" />}
                      <span>
                        {linkPreview.duration > 0
                          ? formatLinkDuration(linkPreview.duration)
                          : linkPreview.metadata_status === 'pending'
                            ? 'Resolving media details...'
                            : 'Duration will be checked by the worker before import'}
                      </span>
                    </div>
                  </div>

                  {resolveLinkImportThumbnailUrl(linkPreview) && (
                    <img
                      src={resolveLinkImportThumbnailUrl(linkPreview)}
                      alt=""
                      onError={(event) => {
                        if (linkPreview.thumbnail_url && event.currentTarget.src !== linkPreview.thumbnail_url) {
                          event.currentTarget.src = linkPreview.thumbnail_url;
                        }
                      }}
                      className="mx-auto w-full max-w-lg rounded-lg bg-slate-100 dark:bg-zinc-900 object-cover shadow-sm"
                    />
                  )}

                  {linkImport && (
                    <div className="mx-auto max-w-lg">
                      <div className="flex items-center justify-between text-xs text-slate-500 dark:text-zinc-400 mb-1">
                        <span className="capitalize">{linkImport.status}</span>
                        <span>{linkImport.progress}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-200 dark:bg-zinc-800 overflow-hidden">
                        <div className="h-full bg-indigo-600 transition-all" style={{ width: `${linkImport.progress}%` }} />
                      </div>
                    </div>
                  )}

                  <Button onClick={startLinkImport} disabled={isStartingLinkImport || !!linkImport} className="mx-auto">
                    {isStartingLinkImport && <Loader2 className="h-4 w-4 animate-spin" />}
                    Start transcription
                  </Button>
                </div>
              )}
              </div>
            )}
          </div>
        );

      case UploadStep.CONFIRM_INFO:
        return (
          <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
            {/* Error Display */}
            {uploadError && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0" />
                <p className="text-red-600 dark:text-red-400 text-sm">{uploadError}</p>
              </div>
            )}

            {/* File Info Card */}
            <Card className="p-1.5 pe-4 flex items-center gap-4 !bg-card !ring-0 !shadow-none">
               {/* Icon Container */}
              <div className="h-14 w-14 bg-raised rounded-[14px] flex items-center justify-center shrink-0">
                {file?.type.startsWith('video') ? <FileVideo className="text-indigo-600 dark:text-indigo-400 h-7 w-7" /> : <FileAudio className="text-indigo-600 dark:text-indigo-400 h-7 w-7" />}
              </div>
              
              {/* Text Info - Explicit Left Alignment */}
              <div className="flex-1 overflow-hidden text-left">
                <p className="font-semibold text-slate-900 dark:text-zinc-100 truncate text-sm mb-0.5" title={file?.name}>{file?.name}</p>
                <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-zinc-400">
                  <span className="uppercase bg-accent/10 px-1.5 py-0.5 rounded-full text-[10px] font-bold text-indigo-600 dark:text-indigo-400 tracking-wide">
                    {file!.name.split('.').pop()}
                  </span>
                  <span>{(file!.size / (1024 * 1024)).toFixed(2)} MB</span>
                </div>
              </div>

              {/* Change Button */}
              <Button variant="ghost" size="sm" onClick={() => { setFile(null); setStep(UploadStep.SELECT_FILE); }} className="text-slate-400 hover:text-indigo-600 hover:bg-white dark:text-zinc-500 dark:hover:text-indigo-400 dark:hover:bg-zinc-800 shrink-0">
                Change
              </Button>
            </Card>

            <div className="bg-card rounded-[18px] divide-y divide-line overflow-visible">
               <div className="p-4 relative z-10">
                  <label className="block text-sm font-semibold text-slate-900 dark:text-zinc-100 mb-2 text-left">Source Language</label>
                  <SearchableSelect
                    options={[
                      { value: 'auto', label: '✨ Auto Detect (Recommended)' },
                      { value: 'en', label: 'English' },
                      { value: 'zh', label: 'Chinese (Mandarin)' },
                      { value: 'es', label: 'Spanish' },
                      { value: 'fr', label: 'French' },
                      { value: 'de', label: 'German' },
                      { value: 'ja', label: 'Japanese' },
                    ]}
                    value={options.language}
                    onChange={(val) => setOptions({...options, language: val})}
                    placeholder="Select language..."
                    searchPlaceholder="Search languages..."
                  />
               </div>

               <div className="p-4 flex items-center justify-between hover:bg-black/[0.02] dark:hover:bg-white/[0.03] transition-colors cursor-pointer rounded-b-[18px]" onClick={() => setOptions({...options, speakerDetection: !options.speakerDetection})}>
                  <div className="flex items-center gap-3 text-left">
                    <div className="bg-emerald-100 dark:bg-emerald-500/10 p-2 rounded-lg shrink-0">
                        <AlertCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div>
                        <p className="text-sm font-medium text-slate-900 dark:text-zinc-100">Speaker Identification</p>
                        <p className="text-xs text-slate-500 dark:text-zinc-400">Detect and label different speakers</p>
                    </div>
                  </div>
                  <div className={`w-11 h-6 rounded-full relative transition-colors duration-200 ${options.speakerDetection ? 'bg-[#34c759]' : 'bg-slate-200 dark:bg-zinc-700'}`}>
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all duration-200 shadow-sm ${options.speakerDetection ? 'left-6' : 'left-1'}`}></div>
                  </div>
               </div>
            </div>
          </div>
        );

      case UploadStep.UPLOADING:
        return (
          <div className="py-2 space-y-8 animate-in fade-in duration-500">
            <div className="flex flex-col items-center justify-center">
                <div className="relative h-24 w-24 mb-4">
                   <svg className="h-full w-full -rotate-90 transform" viewBox="0 0 100 100">
                     <circle cx="50" cy="50" r="45" fill="none" stroke="#ededf0" strokeWidth="8" className="dark:stroke-zinc-800" />
                     <circle cx="50" cy="50" r="45" fill="none" stroke="#007aff" strokeWidth="8" strokeDasharray="283" strokeDashoffset={283 - (283 * progress / 100)} strokeLinecap="round" className="transition-all duration-300 ease-out" />
                   </svg>
                   <div className="absolute inset-0 flex items-center justify-center flex-col">
                     <span className="text-xl font-bold text-slate-900 dark:text-white">{Math.round(progress)}%</span>
                   </div>
                </div>
                <p className="text-slate-500 dark:text-zinc-400 text-sm font-medium animate-pulse">Processing media...</p>
            </div>
            
            <div className="space-y-3 max-w-sm mx-auto">
                {uploadStages.map((stage, idx) => {
                    const isComplete = idx < activeStageIndex;
                    const isCurrent = idx === activeStageIndex;
                    return (
                        <div key={idx} className={`flex items-center gap-3 text-sm transition-all duration-300 ${isComplete || isCurrent ? 'opacity-100' : 'opacity-40'}`}>
                            <div className={`h-5 w-5 rounded-full flex items-center justify-center shrink-0 ${isComplete ? 'bg-emerald-500 text-white' : isCurrent ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-300' : 'bg-slate-100 dark:bg-zinc-800'}`}>
                                {isComplete ? <CheckCircle2 className="h-3.5 w-3.5" /> : isCurrent ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <div className="h-1.5 w-1.5 rounded-full bg-slate-400 dark:bg-zinc-600" />}
                            </div>
                            <span className={`${isComplete ? 'text-slate-900 dark:text-zinc-200' : isCurrent ? 'text-indigo-600 dark:text-indigo-400 font-medium' : 'text-slate-500 dark:text-zinc-500'}`}>
                                {stage.label}
                            </span>
                        </div>
                    );
                })}
            </div>
          </div>
        );
      
      case UploadStep.COMPLETE:
        return (
            <div className="py-12 text-center animate-in zoom-in-95 duration-300">
                <div className="relative inline-block mb-6">
                    <div className="absolute inset-0 bg-emerald-100 dark:bg-emerald-500/20 rounded-full animate-ping opacity-20"></div>
                    <div className="h-20 w-20 bg-emerald-50 dark:bg-emerald-500/10 rounded-full flex items-center justify-center relative z-10">
                        <CheckCircle2 className="h-10 w-10 text-emerald-500 dark:text-emerald-400" />
                    </div>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                  {instantUpload ? 'Instant Upload Successful!' : 'Upload Successful!'}
                </h3>
                <p className="text-slate-500 dark:text-zinc-400 max-w-xs mx-auto mb-8">
                  {instantUpload
                    ? 'Your file was already available, and transcription is starting now.'
                    : 'Your file has been secured and is now being transcribed by our AI engine.'}
                </p>
                <div className="flex justify-center items-center gap-2 text-sm text-indigo-600 dark:text-indigo-300 font-medium bg-indigo-50 dark:bg-indigo-500/10 py-2 px-4 rounded-full mx-auto w-fit">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Redirecting to editor...
                </div>
            </div>
        )
      default: return null;
    }
  };

  const renderFooter = () => {
     if (step === UploadStep.SELECT_FILE) return null;
     if (step === UploadStep.UPLOADING || step === UploadStep.COMPLETE) return null;
     
     return (
        <>
            <Button variant="ghost" onClick={() => setStep(step - 1)}>Back</Button>
            <Button onClick={startUpload}>Start Transcription</Button>
        </>
     )
  };

  const modalContent = (
    <div className="flex flex-col h-full">
      <div className={`p-6 ${step === UploadStep.COMPLETE ? 'pt-10' : ''}`}>
          {renderContent()}
      </div>

      {(step === UploadStep.CONFIRM_INFO) && (
           <div className="px-6 pb-6 pt-1 flex justify-end gap-2.5 mt-auto">
              {renderFooter()}
           </div>
      )}
    </div>
  );

  if (embedded) {
    return (
      <div className="w-full max-w-2xl mx-auto bg-raised rounded-[24px] shadow-window text-start transition-all">
        {step !== UploadStep.SELECT_FILE && step !== UploadStep.COMPLETE && (
             <div className="px-6 py-4 border-b border-slate-100 dark:border-zinc-800 bg-white dark:bg-zinc-900 flex justify-between items-center">
                <h3 className="font-semibold text-lg text-slate-900 dark:text-white">Transcription Options</h3>
                <Button variant="ghost" size="sm" onClick={() => setStep(UploadStep.SELECT_FILE)}>Cancel</Button>
             </div>
        )}
        {modalContent}
      </div>
    );
  }

  return (
    <Modal 
        isOpen={isOpen} 
        onClose={onClose} 
        title={step === UploadStep.SELECT_FILE ? "Upload Media" : "Transcription Options"}
        footer={null} // We handle footer manually inside for custom styling
    >
       {/* Override Modal default padding/structure if needed, but here we just render content */}
       {/* Since Modal component adds its own structure, we adapt. 
           The existing Modal component has padding and header. 
           We might need to adjust the content to fit nicely.
       */}
       <div className="-m-6">
         {modalContent}
       </div>
    </Modal>
  );
};

export default UploadFlow;
